/**
 * The login page's marketing panel — the product identity a resolved tenant
 * sells, or nothing at all when the host names no tenant we can trust.
 *
 * The state contract itself lives in `./login-page-state`.
 */
import { useTranslation } from 'react-i18next';

import { BrandLockup } from '@/components/branding/BrandLockup';
import { Skeleton } from '@/components/ui/skeleton';
import type { LoginProduct } from './login-product';
import { namesTenant, type LoginPageState } from './login-page-state';

const FEATURES = ['feature1', 'feature2', 'feature3'] as const;

export interface LoginProductPanelProps {
  state: LoginPageState;
}

export function LoginProductPanel({ state }: Readonly<LoginProductPanelProps>) {
  return (
    <div className="hidden lg:flex lg:w-[55%] relative items-center justify-center p-12">
      <div className="relative z-10 max-w-lg space-y-8">
        <PanelBody state={state} />
      </div>
    </div>
  );
}

function PanelBody({ state }: Readonly<LoginProductPanelProps>) {
  if (state.kind === 'product')
    return <ProductShowcase product={state.product} />;
  if (state.kind === 'loading') return <PanelSkeleton />;
  return namesTenant(state) ? <BrandLockup size="lg" /> : null;
}

function ProductShowcase({ product }: Readonly<{ product: LoginProduct }>) {
  const { t } = useTranslation();
  const copy = `auth.login.products.${product}`;

  return (
    <>
      <div className="space-y-4">
        <BrandLockup size="lg" />
        <h1 className="text-5xl font-bold tracking-tight leading-[1.1]">
          <span>{t(`${copy}.headlineLine1`)}</span>
          <br />
          <span className="text-primary">{t(`${copy}.headlineLine2`)}</span>
        </h1>
        <p className="text-lg text-muted-foreground leading-relaxed max-w-md">
          {t(`${copy}.blurb`)}
        </p>
      </div>

      <div className="space-y-4 pt-4">
        {FEATURES.map((feature) => (
          <div
            key={feature}
            className="flex items-start gap-4 p-4 rounded-xl bg-[hsl(var(--surface-container-low)/0.5)] backdrop-blur-sm border border-[hsl(var(--foreground)/0.06)] hover:border-primary/20 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5"
          >
            <span className="text-2xl mt-0.5">
              {t(`${copy}.${feature}.icon`)}
            </span>
            <div>
              <h3 className="font-semibold text-sm">
                {t(`${copy}.${feature}.title`)}
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t(`${copy}.${feature}.description`)}
              </p>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function PanelSkeleton() {
  return (
    <>
      <div className="space-y-4">
        <Skeleton className="h-12 w-44" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-4/5" />
        <Skeleton className="h-16 w-full max-w-md" />
      </div>
      <div className="space-y-4 pt-4">
        {FEATURES.map((feature) => (
          <Skeleton key={feature} className="h-[76px] w-full rounded-xl" />
        ))}
      </div>
    </>
  );
}
